import { Title, Stack, Group, Button, Badge, ActionIcon, Text } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconRefresh, IconBan } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deadletterApi } from "../api/deadletter.js";
import { notifications } from "@mantine/notifications";
import { useState } from "react";

const STATUS_FILTERS = ["all", "pending", "retrying", "resolved", "abandoned"];

export function DeadLetter() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("pending");
  const { data, isLoading } = useQuery({
    queryKey: ["deadletter", filter],
    queryFn: () => deadletterApi.list(filter === "all" ? undefined : filter),
  });

  const retry = useMutation({
    mutationFn: (id: string) => deadletterApi.retry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deadletter"] });
      notifications.show({ title: "Retry kicked off", message: "Watch back in a few seconds", color: "blue" });
    },
  });
  const abandon = useMutation({
    mutationFn: (id: string) => deadletterApi.abandon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deadletter"] }),
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Dead-letter ({data?.length ?? 0})</Title>
        <Group gap="xs">
          {STATUS_FILTERS.map(s => (
            <Button key={s} size="xs" variant={filter === s ? "filled" : "default"} onClick={() => setFilter(s)}>{s}</Button>
          ))}
        </Group>
      </Group>
      <DataTable
        striped withTableBorder fetching={isLoading} idAccessor="messageId"
        records={data ?? []}
        columns={[
          { accessor: "messageId", title: "Message ID", render: (r) => <code style={{ fontSize: 11 }}>{r.messageId.slice(0, 32)}…</code> },
          { accessor: "status", title: "Status", render: (r) => <Badge>{r.status}</Badge> },
          { accessor: "retryCount", title: "Retries", textAlign: "right", width: 80 },
          { accessor: "erroredAt", title: "First error", render: (r) => new Date(r.erroredAt).toLocaleString() },
          { accessor: "errorMessage", title: "Error", render: (r) => <Text size="xs" lineClamp={2}>{r.errorMessage}</Text> },
          { accessor: "actions", title: "", width: 120, textAlign: "right",
            render: (r) => {
              const retryBusy = retry.isPending && retry.variables === r.messageId;
              const abandonBusy = abandon.isPending && abandon.variables === r.messageId;
              const isFinal = r.status === "resolved" || r.status === "abandoned";
              return (
                <Group gap="xs" justify="flex-end">
                  <ActionIcon variant="subtle" color="blue" loading={retryBusy} disabled={isFinal || retryBusy || abandonBusy} onClick={() => retry.mutate(r.messageId)} title="Retry"><IconRefresh size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" loading={abandonBusy} disabled={isFinal || retryBusy || abandonBusy} onClick={() => { if (confirm(`Abandon ${r.messageId}? This marks it final and stops further retries.`)) abandon.mutate(r.messageId); }} title="Abandon"><IconBan size={16} /></ActionIcon>
                </Group>
              );
            },
          },
        ]}
      />
    </Stack>
  );
}
